import { createTheme } from '@mui/material/styles';
import type { Theme, ThemeOptions } from '@mui/material/styles';

const commonThemeOptions: ThemeOptions = {
  typography: {
    fontFamily: ['Roboto', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(','),
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    button: {
      textTransform: 'none' as const,
    },
    // Responsive typography variants
    h1: {
      fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
    },
    h2: {
      fontSize: 'clamp(1.25rem, 3vw, 2rem)',
    },
    h3: {
      fontSize: 'clamp(1.1rem, 2.5vw, 1.75rem)',
    },
    h4: {
      fontSize: 'clamp(1rem, 2vw, 1.5rem)',
    },
    h5: {
      fontSize: 'clamp(0.9rem, 1.5vw, 1.25rem)',
    },
    h6: {
      fontSize: 'clamp(0.85rem, 1.2vw, 1.1rem)',
    },
    body1: {
      fontSize: 'clamp(0.875rem, 1.2vw, 1rem)',
    },
    body2: {
      fontSize: 'clamp(0.8rem, 1vw, 0.95rem)',
    },
    caption: {
      fontSize: 'clamp(0.65rem, 0.9vw, 0.85rem)',
    },
    overline: {
      fontSize: 'clamp(0.6rem, 0.8vw, 0.75rem)',
    },
    // ===== CARD STRUCTURE (Custom Variants) =====
    /** Main card titles: "CHAMBER", "CAMERAS", "EXPERIMENT" */
    cardName: {
      fontSize: 'clamp(0.8rem, 1.2vw + 0.55rem, 1.0rem)',
      fontWeight: 700,
    },
    /** Entity IDs: exp_id, camera_id, chamber_id */
    entityId: {
      fontSize: 'clamp(0.6rem, 1vw + 0.6rem, 0.8rem)',
      fontWeight: 700,
    },
    /** Section headers: "EXPERIMENT ID", "DETAILS", "CONTROLS", "MONITORING", etc. */
    sectionHeader: {
      fontSize: 'clamp(0.65rem, 0.85vw + 0.5rem, 0.8rem)',
      fontWeight: 700,
    },
    /** Field labels below inputs: "TEST ID", "PROFILE ID", "NUM PROFILES", "START TIME", etc. */
    fieldLabel: {
      fontSize: 'clamp(0.45rem, 0.6vw + 0.4rem, 0.6rem)',
      fontWeight: 500,
    },
    /** Text inside input fields (TextField inputs) */
    fieldText: {
      fontSize: 'clamp(0.5rem, 0.85vw + 0.5rem, 0.7rem)',
      fontWeight: 700,
    },
    /** Button text: "RUN", "STOP", "PAUSE", "RESUME", etc. */
    buttonLabel: {
      fontSize: 'clamp(0.6rem, 0.75vw + 0.45rem, 0.75rem)',
      fontWeight: 700,
    },
    /** Alert-related text (values and descriptions) */
    alertLabel: {
      fontSize: 'clamp(0.5rem, 0.65vw + 0.35rem, 0.6rem)',
      fontWeight: 700,
    },
    // ===== ICON LABELS (Standard for STATUS/HEALTH/ALERT indicators) =====
    /** Icon value text: The actual status/health/alert value (e.g., "AVAILABLE", "OK", "HIGH") */
    iconValueText: {
      fontSize: 'clamp(0.6rem, 0.65vw + 0.5rem, 0.65rem)',
      fontWeight: 700,
    },
    /** Icon label text: The label below icon (e.g., "STATUS", "HEALTH", "ALERT") */
    iconLabelText: {
      fontSize: 'clamp(0.55rem, 0.6vw + 0.45rem, 0.6rem)',
      fontWeight: 400,
    },
    // ===== LOG SYSTEM (Custom Variants) =====
    /** Log level badges: "INFO", "DEBUG", "WARNING", "ERROR" (bold, colored) */
    logSection: {
      fontSize: 'clamp(0.45rem, 0.6vw + 0.3rem, 0.55rem)',
      fontWeight: 700,
    },
    /** Actual log message text (monospace) */
    logText: {
      fontSize: 'clamp(0.6rem, 0.75vw + 0.4rem, 0.75rem)',
      fontFamily: 'monospace',
    },
  },
  spacing: 4,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  // ===== ICON SIZES =====
  iconSizes: {
    small: 'clamp(14px, 1vw + 12px, 16px)',
    medium: 'clamp(18px, 1.2vw + 16px, 20px)',
    large: 'clamp(22px, 1.5vw + 20px, 24px)',
  },
  // ===== FIELD LABEL CONTAINER & POSITIONING (Standardized) =====
  /** Container Box styles for TextField with positioned label below */
  fieldLabelContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    flex: 1,
    paddingBottom: '2px',
  },
  /** Typography positioning styles for field labels positioned below TextFields */
  fieldLabelPositioned: {
    position: 'absolute',
    top: '100%',
    left: '8px',
    marginTop: '0px',
    color: 'text.secondary',
    backgroundColor: 'white',
    padding: '0 2px',
    lineHeight: 1.0,
    whiteSpace: 'normal',
  },
};

const createThemeWithScrollbars = (options: ThemeOptions): Theme => {
  const theme = createTheme(options);
  return createTheme({
    ...theme,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          a: {
            color: 'inherit',
            textDecoration: 'none',
            '&:visited': {
              color: 'inherit',
            },
          },
          body: {
            scrollbarColor: `${theme.palette.primary.main} ${theme.palette.background.default}`,
            '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
              width: 8,
            },
            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
              borderRadius: 8,
              backgroundColor: theme.palette.primary.main,
              minHeight: 24,
            },
            '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
              backgroundColor: theme.palette.primary.dark,
            },
            '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
              backgroundColor: theme.palette.primary.dark,
            },
            '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
            '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
              backgroundColor: theme.palette.background.default,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: '2px solid #bdbdbd', // bold line between rows
            borderRight: '1px solid #e0e0e0', // subtle line between columns
            '&:last-of-type': {
              borderRight: 0,
            },
            fontSize: 'clamp(0.45rem, 0.6vw + 0.6rem, 0.8rem)', // fieldLabel size for body cells (smaller)
          },
          head: {
            backgroundColor: '#e0e0e0', // more pronounced header background
            fontWeight: 'bold',
            fontSize: 'clamp(0.65rem, 0.85vw + 0.5rem, 0.8rem)', // sectionHeader size for headers
          },
        },
      },
    },
  });
};

const theme = createThemeWithScrollbars({
  ...commonThemeOptions,
  palette: {
    mode: 'light',
    primary: {
      main: '#546e7a',
      light: '#819ca9',
      dark: '#29434e',
    },
    secondary: {
      main: '#78909c',
      light: '#a7c0cd',
      dark: '#4b636e',
    },
    background: {
      default: '#eceff1',
      paper: '#f5f5f5',
    },
    text: {
      primary: '#37474f',
      secondary: '#546e7a',
    },
    action: {
      active: '#546e7a',
      hover: 'rgba(84, 110, 122, 0.08)',
    },
    divider: '#cfd8dc',
  },
});

const darkTheme = createThemeWithScrollbars({
  ...commonThemeOptions,
  palette: {
    mode: 'dark',
    primary: {
      main: '#1DA1F2',
    },
    secondary: {
      main: '#E5989B',
    },
    background: {
      default: '#15202B',
      paper: '#192734',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#8899A6',
    },
    action: {
      active: '#1DA1F2',
      hover: 'rgba(29, 161, 242, 0.1)',
    },
    divider: '#38444D',
  },
});

export { theme, darkTheme };
